// GameClient is the high-level, type-safe SDK surface used by the game client.
//
// Trust model: the SERVER is authoritative for balance, symbols, reel stops,
// wins, free spins, total payout, and round status. The SDK performs NO payout
// or balance math and stores NO secrets, wallet data, or operator credentials.
// The short-lived access token is held in MEMORY ONLY (never localStorage).
import { GameError, ValidationError } from './errors.js';
import { Transport } from './http.js';
import { isValidIdempotencyKey, newIdempotencyKey } from './ids.js';
import { BalanceResultSchema, CloseResultSchema, ExchangeResultSchema, HeartbeatResultSchema, PublicReplaySchema, RecoveryResultSchema, SpinResultSchema, } from './schema.js';
export class GameClient {
    transport;
    origin;
    accessToken;
    session;
    constructor(config) {
        this.transport = new Transport({
            baseUrl: config.gatewayBaseUrl,
            defaultTimeoutMs: config.timeoutMs ?? 15000,
            ...(config.fetchImpl !== undefined ? { fetchImpl: config.fetchImpl } : {}),
        });
        this.origin = config.origin;
    }
    /** Whether an access token is currently held in memory. */
    hasSession() {
        return this.accessToken !== undefined;
    }
    /** The current session info (without the token), if exchanged. */
    currentSession() {
        return this.session;
    }
    /**
     * Exchanges a one-time launch token for a short-lived access token, which is
     * stored in memory. Returns the (non-secret) session info.
     */
    async exchangeLaunchToken(launchToken, opts) {
        if (!launchToken) {
            throw new GameError('bad_request', 'launch token required');
        }
        const result = await this.transport.request({
            method: 'POST',
            path: '/v1/launch/exchange',
            schema: ExchangeResultSchema,
            body: { launch_token: launchToken },
            headers: this.baseHeaders(),
            ...this.callOpts(opts),
        });
        this.accessToken = result.access_token;
        // Keep session info WITHOUT the token for display/recovery use.
        this.session = { ...result, access_token: '' };
        return result;
    }
    /**
     * Submits a spin. Generates an idempotency key when none is supplied and
     * returns it so the caller can persist it for a safe same-key retry. This
     * method never retries under a different key.
     */
    async spin(params) {
        this.requireSession();
        if (!Number.isInteger(params.betMinor) || params.betMinor <= 0) {
            throw new GameError('bad_request', 'betMinor must be a positive integer');
        }
        const idempotencyKey = params.idempotencyKey ?? newIdempotencyKey();
        if (!isValidIdempotencyKey(idempotencyKey)) {
            throw new GameError('bad_request', 'invalid idempotency key');
        }
        const result = await this.transport.request({
            method: 'POST',
            path: '/v1/spins',
            schema: SpinResultSchema,
            body: { bet_minor: params.betMinor },
            headers: { ...this.authHeaders(), 'Idempotency-Key': idempotencyKey },
            ...this.callOpts(params),
        });
        return { result, idempotencyKey };
    }
    /**
     * Rolls the Dice game via the dedicated authoritative action. Carries the
     * player's selected number (1..6); the server is authoritative for the dice,
     * match count, multiplier, and payout. Generates an idempotency key when none
     * is supplied and returns it for a safe same-key retry. Never auto-retries
     * under a different key.
     */
    async roll(params) {
        this.requireSession();
        if (!Number.isInteger(params.selectedNumber) || params.selectedNumber < 1 || params.selectedNumber > 6) {
            throw new GameError('bad_request', 'selectedNumber must be an integer in 1..6');
        }
        if (!Number.isInteger(params.betMinor) || params.betMinor <= 0) {
            throw new GameError('bad_request', 'betMinor must be a positive integer');
        }
        const idempotencyKey = params.idempotencyKey ?? newIdempotencyKey();
        if (!isValidIdempotencyKey(idempotencyKey)) {
            throw new GameError('bad_request', 'invalid idempotency key');
        }
        const result = await this.transport.request({
            method: 'POST',
            path: '/v1/games/dice/roll',
            schema: SpinResultSchema,
            body: { selected_number: params.selectedNumber, bet_minor: params.betMinor },
            headers: { ...this.authHeaders(), 'Idempotency-Key': idempotencyKey },
            ...this.callOpts(params),
        });
        return { result, idempotencyKey };
    }
    /** Rolls Lightning Dice via the dedicated authoritative action. */
    async rollLightningDice(params) {
        this.requireSession();
        if (!Number.isInteger(params.selectedNumber) || params.selectedNumber < 1 || params.selectedNumber > 6) {
            throw new GameError('bad_request', 'selectedNumber must be an integer in 1..6');
        }
        if (!Number.isInteger(params.betMinor) || params.betMinor <= 0) {
            throw new GameError('bad_request', 'betMinor must be a positive integer');
        }
        const idempotencyKey = params.idempotencyKey ?? newIdempotencyKey();
        if (!isValidIdempotencyKey(idempotencyKey)) {
            throw new GameError('bad_request', 'invalid idempotency key');
        }
        const result = await this.transport.request({
            method: 'POST',
            path: '/v1/games/lightning-dice/roll',
            schema: SpinResultSchema,
            body: { selected_number: params.selectedNumber, bet_minor: params.betMinor },
            headers: { ...this.authHeaders(), 'Idempotency-Key': idempotencyKey },
            ...this.callOpts(params),
        });
        return { result, idempotencyKey };
    }
    async rollLightningNumberDice(params) {
        this.requireSession();
        if (!Number.isInteger(params.selectedNumber) || params.selectedNumber < 1 || params.selectedNumber > 6) {
            throw new GameError('bad_request', 'selectedNumber must be an integer in 1..6');
        }
        if (!Number.isInteger(params.betMinor) || params.betMinor <= 0) {
            throw new GameError('bad_request', 'betMinor must be a positive integer');
        }
        const idempotencyKey = params.idempotencyKey ?? newIdempotencyKey();
        if (!isValidIdempotencyKey(idempotencyKey)) {
            throw new GameError('bad_request', 'invalid idempotency key');
        }
        const result = await this.transport.request({
            method: 'POST',
            path: '/v1/games/lightning-number-dice/roll',
            schema: SpinResultSchema,
            body: { selected_number: params.selectedNumber, bet_minor: params.betMinor },
            headers: { ...this.authHeaders(), 'Idempotency-Key': idempotencyKey },
            ...this.callOpts(params),
        });
        return { result, idempotencyKey };
    }
    /** Races Seahorse Race via the dedicated authoritative action. */
    async race(params) {
        this.requireSession();
        if (!Number.isInteger(params.selectedLane) || params.selectedLane < 1 || params.selectedLane > 3) {
            throw new GameError('bad_request', 'selectedLane must be an integer in 1..3');
        }
        if (!Number.isInteger(params.targetMultiplierHundredths) ||
            params.targetMultiplierHundredths < 101 ||
            params.targetMultiplierHundredths > 1000) {
            throw new GameError('bad_request', 'targetMultiplierHundredths must be an integer in 101..1000');
        }
        if (!Number.isInteger(params.betMinor) || params.betMinor <= 0) {
            throw new GameError('bad_request', 'betMinor must be a positive integer');
        }
        const idempotencyKey = params.idempotencyKey ?? newIdempotencyKey();
        if (!isValidIdempotencyKey(idempotencyKey)) {
            throw new GameError('bad_request', 'invalid idempotency key');
        }
        const result = await this.transport.request({
            method: 'POST',
            path: '/v1/games/seahorse-race/race',
            schema: SpinResultSchema,
            body: {
                selected_lane: params.selectedLane,
                target_multiplier_hundredths: params.targetMultiplierHundredths,
                bet_minor: params.betMinor,
            },
            headers: { ...this.authHeaders(), 'Idempotency-Key': idempotencyKey },
            ...this.callOpts(params),
        });
        return { result, idempotencyKey };
    }
    /**
     * Fetches the server-authoritative balance. The client uses this to display
     * the confirmed balance after launch and after recovery; it performs NO local
     * balance computation and never contacts the operator wallet directly.
     */
    async getBalance(opts) {
        this.requireSession();
        return this.transport.request({
            method: 'GET',
            path: '/v1/balance',
            schema: BalanceResultSchema,
            headers: this.authHeaders(),
            ...this.callOpts(opts),
        });
    }
    /** Retrieves the authoritative state of a round by id. */
    async getRound(roundId, opts) {
        this.requireSession();
        if (!roundId) {
            throw new GameError('bad_request', 'roundId required');
        }
        return this.transport.request({
            method: 'GET',
            path: `/v1/rounds/${encodeURIComponent(roundId)}`,
            schema: SpinResultSchema,
            headers: this.authHeaders(),
            ...this.callOpts(opts),
        });
    }
    /** Returns any unresolved round for the player (used for reconnect/recovery). */
    async getRecovery(opts) {
        this.requireSession();
        return this.transport.request({
            method: 'GET',
            path: '/v1/recovery',
            schema: RecoveryResultSchema,
            headers: this.authHeaders(),
            ...this.callOpts(opts),
        });
    }
    /**
     * Recovery helper for browser reload: returns the unresolved round's
     * authoritative state if one exists, else null. Never creates a spin.
     */
    async recoverAfterReload(opts) {
        const rec = await this.getRecovery(opts);
        if (!rec.has_unresolved || !rec.round) {
            return null;
        }
        return rec.round;
    }
    /** Refreshes the session keep-alive. */
    async heartbeat(opts) {
        this.requireSession();
        return this.transport.request({
            method: 'POST',
            path: '/v1/heartbeat',
            schema: HeartbeatResultSchema,
            body: {},
            headers: this.authHeaders(),
            ...this.callOpts(opts),
        });
    }
    /** Closes the session and clears the in-memory access token. */
    async closeSession(opts) {
        this.requireSession();
        try {
            return await this.transport.request({
                method: 'POST',
                path: '/v1/session/close',
                schema: CloseResultSchema,
                body: {},
                headers: this.authHeaders(),
                ...this.callOpts(opts),
            });
        }
        finally {
            this.clearSession();
        }
    }
    /**
     * Fetches a PUBLIC replay by its public id. UNAUTHENTICATED: requires no
     * session and never sends an access token. Used by the standalone replay page
     * to reconstruct a completed round's outcome from authoritative stored data.
     */
    async fetchPublicReplay(replayId, opts) {
        if (!replayId) {
            throw new GameError('bad_request', 'replayId required');
        }
        return this.transport.request({
            method: 'GET',
            path: `/public/replays/${encodeURIComponent(replayId)}`,
            schema: PublicReplaySchema,
            headers: this.baseHeaders(),
            ...this.callOpts(opts),
        });
    }
    /** Forgets the in-memory token without a network call. */
    clearSession() {
        this.accessToken = undefined;
        this.session = undefined;
    }
    requireSession() {
        if (this.accessToken === undefined) {
            throw new ValidationError('no active session; call exchangeLaunchToken first');
        }
    }
    baseHeaders() {
        return this.origin !== undefined ? { Origin: this.origin } : {};
    }
    authHeaders() {
        return { ...this.baseHeaders(), Authorization: `Bearer ${this.accessToken ?? ''}` };
    }
    callOpts(opts) {
        if (!opts)
            return {};
        return {
            ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
            ...(opts.timeoutMs !== undefined ? { timeoutMs: opts.timeoutMs } : {}),
        };
    }
}
//# sourceMappingURL=client.js.map